/**
 * Cascade Match Path Utility
 *
 * Provides shared logic for generating cascade match paths from chip selections
 * in delete, update, and paste cascade dialogs.
 */

export interface CascadeChip {
    name: string;
    tag: string;
    selected: boolean;
}

export interface MatchPathResult {
    ancestorMatchPath?: string;
    itemMatchPath: string;
}

/**
 * Generates a match path from selected bottom chips based on the new cascade match path logic.
 *
 * This new logic handles:
 * - All chips selected: returns full itemMatchPath
 * - Single chip selected: returns leaf chip as itemMatchPath
 * - Contiguous selections: returns all selected chips as itemMatchPath
 * - Non-contiguous selections: splits into ancestorMatchPath and itemMatchPath based on gaps
 *
 * @param selectedBottomChips - Array of selected chips
 * @param allBottomChips - Array of all bottom chips (for context)
 * @returns Object containing ancestorMatchPath and/or itemMatchPath
 */
function getPathNew(selectedBottomChips: CascadeChip[], allBottomChips: CascadeChip[]): MatchPathResult {
    let ancestorMatchPath = '';
    let itemMatchPath = '';
    const leafChipTag = allBottomChips[allBottomChips.length - 1].tag;

    // Case 1: All chips are selected
    if (selectedBottomChips.length === allBottomChips.length) {
        itemMatchPath = selectedBottomChips.map(chip => `/${chip.tag}`).join('');
        return {itemMatchPath};
    }

    // Case 2: Only one chip is selected
    if (selectedBottomChips.length === 1) {
        itemMatchPath = `/${leafChipTag}`;
        return {itemMatchPath};
    }

    // Case 3: Multiple chips selected - check for contiguity
    // Check if selection is contiguous (no gaps)
    let isContiguous = true;
    let lastSelectedIndex = -1;

    for (let i = 0; i < allBottomChips.length; i++) {
        if (allBottomChips[i].selected) {
            if (lastSelectedIndex !== -1 && i !== lastSelectedIndex + 1) {
                // Gap found between selected chips
                isContiguous = false;
                break;
            }
            lastSelectedIndex = i;
        }
    }

    // If selection is contiguous (no gaps), put everything in itemMatchPath
    // This matches the old logic behavior for contiguous selections
    if (isContiguous) {
        itemMatchPath = selectedBottomChips.map(chip => `/${chip.tag}`).join('');
        return {itemMatchPath};
    }

    // Case 4: Non-contiguous selection - split into ancestorMatchPath and itemMatchPath
    let previousSelectedIndex = -1;
    let stillContiguous = true;

    for (let i = 0; i < allBottomChips.length; i++) {
        const chip = allBottomChips[i];
        if (!chip.selected) {
            // A gap ends contiguity
            if (previousSelectedIndex !== -1) {
                stillContiguous = false;
            }
            continue;
        }

        const isNextInSequence = previousSelectedIndex === -1 || i === previousSelectedIndex + 1;

        if (stillContiguous && isNextInSequence && chip.tag !== leafChipTag) {
            ancestorMatchPath += `/${chip.tag}`;
        } else {
            stillContiguous = false;
            itemMatchPath += `/${chip.tag}`;
        }

        previousSelectedIndex = i;
    }

    return {
        ancestorMatchPath,
        itemMatchPath
    };
}

/**
 * Generates a match path from selected bottom chips based on the old cascade match path logic.
 *
 * This old logic handles:
 * - All chips selected: returns full itemMatchPath
 * - Consecutive selections from start: splits into ancestorMatchPath and itemMatchPath
 * - Non-consecutive selections: attempts to build both paths with complex index-based logic
 *
 * @param selectedBottomChips - Array of selected chips
 * @param allBottomChips - Array of all bottom chips (for context)
 * @returns Object containing ancestorMatchPath and/or itemMatchPath
 */
function getPathOld(selectedBottomChips: CascadeChip[], allBottomChips: CascadeChip[]): MatchPathResult {
    let fullTagPath = '';
    let ancestorMatchPath = '';
    let itemMatchPath = '';

    // Case 1: All chips are selected
    if (selectedBottomChips.length === allBottomChips.length) {
        for (const chip of selectedBottomChips) {
            fullTagPath += `/${chip.tag}`;
        }
        return {itemMatchPath: fullTagPath};
    }

    // Case 2: Partial selection
    for (const chip of selectedBottomChips) {
        // if consecutive (selected index matches position in all chips), add to ancestor match path
        if (selectedBottomChips.indexOf(chip) === allBottomChips.indexOf(chip)) {
            ancestorMatchPath += `/${chip.tag}`;
        }
        // if not consecutive, add to item match path
        else {
            if (selectedBottomChips.length > 1) {
                ancestorMatchPath.includes(selectedBottomChips[0].tag) ? ancestorMatchPath += `` :
                    ancestorMatchPath += `/${selectedBottomChips[0].tag}`;

                for (let i = 1; i < selectedBottomChips.length; i++) {
                    itemMatchPath.includes(selectedBottomChips[i].tag) ||
                    ancestorMatchPath.includes(selectedBottomChips[i].tag) ? itemMatchPath += `` :
                        itemMatchPath += `/${selectedBottomChips[i].tag}`;
                }
            } else {
                itemMatchPath += `/${chip.tag}`;
            }
        }
    }

    return {
        ancestorMatchPath,
        itemMatchPath
    };
}

/**
 * Generates a match path from selected chips based on the feature flag setting.
 *
 * This is the main entry point for the cascade match path generation logic.
 * It delegates to either the new or old implementation based on the feature flag.
 *
 * @param selectedBottomChips - Array of selected chips
 * @param allBottomChips - Array of all bottom chips (for context)
 * @param useNewLogic - Feature flag indicating whether to use new match path logic
 * @returns Object containing ancestorMatchPath and/or itemMatchPath
 */
export function getCascadeMatchPath(
    selectedBottomChips: CascadeChip[],
    allBottomChips: CascadeChip[],
    useNewLogic: boolean
): MatchPathResult {
    if (useNewLogic) {
        return getPathNew(selectedBottomChips, allBottomChips);
    } else {
        return getPathOld(selectedBottomChips, allBottomChips);
    }
}
